import { auth } from '../firebase';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export interface GHLConfig {
  connected: boolean;
  location_id: string | null;
  location_name: string | null;
  webhook_url: string | null;
  connected_at: string | null;
  last_sync: { completed_at: string; contacts_synced: number } | null;
}

export interface SyncStatus {
  status: 'idle' | 'running' | 'completed' | 'failed';
  progress: {
    total_contacts: number;
    processed: number;
    created: number;
    updated: number;
    skipped: number;
    percent: number;
  };
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  last_sync: { completed_at: string; contacts_synced: number } | null;
}

export async function getGHLConfig(tenantId: string): Promise<GHLConfig> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/ghl/config`, { headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to get GHL config: ${res.statusText}`);
  }
  return res.json();
}

export async function connectGHL(
  tenantId: string,
  locationId: string,
  pitToken: string,
): Promise<{ success: boolean; location_name: string; webhook_url: string }> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/ghl/connect`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ location_id: locationId, pit_token: pitToken }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to connect GHL: ${res.statusText}`);
  }
  return res.json();
}

export async function disconnectGHL(tenantId: string): Promise<void> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/ghl/disconnect`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to disconnect GHL: ${res.statusText}`);
  }
}

export async function triggerGHLSync(
  tenantId: string,
): Promise<{ sync_job_id: string; tenant_id: string; message: string }> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/ghl/sync`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to trigger sync: ${res.statusText}`);
  }
  return res.json();
}

export async function getGHLSyncStatus(tenantId: string): Promise<SyncStatus> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/ghl/sync/status`, {
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to get sync status: ${res.statusText}`);
  }
  return res.json();
}
